import type { IconProps } from '@chakra-ui/react';
import { Button, Flex, Heading, Icon, Stack, Text } from '@chakra-ui/react';
import * as React from 'react';

import { MentorshipModuleIcon } from '@/modules/mentorship/common/presentation/MentorshipModulePresentation';

export type MentorshipEmptyStateIconVariant =
  | 'program'
  | 'goal'
  | 'task'
  | 'session'
  | 'activity'
  | 'resource'
  | 'application'
  | 'template'
  | 'matching'
  | 'mentor'
  | 'mentee'
  | 'participants'
  | 'cycle'
  | 'search'
  | 'notFound';

export const MentorshipEmptyState: React.FC<{
  title: string;
  description?: string;
  icon?: MentorshipEmptyStateIconVariant;
  actionLabel?: string;
  secondaryActionLabel?: string;
  onAction?: () => void;
  onSecondaryAction?: () => void;
  actionLoading?: boolean;
  compact?: boolean;
}> = ({
  title,
  description,
  icon = 'program',
  actionLabel,
  secondaryActionLabel,
  onAction,
  onSecondaryAction,
  actionLoading = false,
  compact = false,
}) => (
  <Stack
    align="center"
    spacing={compact ? '3' : '4'}
    py={compact ? '6' : '10'}
    px="4"
    textAlign="center"
  >
    {icon === 'mentor' || icon === 'mentee' ? (
      <Flex
        boxSize={compact ? '48px' : '56px'}
        flexShrink={0}
        align="center"
        justify="center"
        borderRadius="xl"
        bg="primary.50"
        color="primary.500"
        borderWidth="1px"
        borderColor="primary.100"
      >
        <MentorshipModuleIcon
          module={icon}
          boxSize={compact ? '24px' : '28px'}
        />
      </Flex>
    ) : (
      <MentorshipEmptyStateIcon
        variant={icon}
        boxSize={compact ? '72px' : '96px'}
      />
    )}
    <Stack spacing="2" maxW="560px">
      <Heading size={compact ? 'sm' : 'md'} color="gray.900">
        {title}
      </Heading>
      {description ? (
        <Text color="gray.500" fontSize="sm">
          {description}
        </Text>
      ) : null}
    </Stack>
    {actionLabel || secondaryActionLabel ? (
      <Stack direction={{ base: 'column', sm: 'row' }} spacing="3">
        {actionLabel ? (
          <Button
            colorScheme="primary"
            onClick={onAction}
            isLoading={actionLoading}
          >
            {actionLabel}
          </Button>
        ) : null}
        {secondaryActionLabel ? (
          <Button variant="outline" onClick={onSecondaryAction}>
            {secondaryActionLabel}
          </Button>
        ) : null}
      </Stack>
    ) : null}
  </Stack>
);

export const MentorshipEmptyStateIcon: React.FC<
  IconProps & { variant?: MentorshipEmptyStateIconVariant }
> = ({ variant = 'program', ...props }) => (
  <Icon viewBox="0 0 96 96" fill="none" aria-hidden="true" {...props}>
    <rect
      x="16"
      y="8"
      width="64"
      height="64"
      rx="16"
      fill="#EFF6FF"
      stroke="#BFDBFE"
    />
    <circle
      cx="72"
      cy="18"
      r="12"
      fill="#FBBF24"
      stroke="white"
      strokeWidth="3"
    />
    <path
      d="M72 12.5l1.8 3.6 4 .6-2.9 2.8.7 4-3.6-1.9-3.6 1.9.7-4-2.9-2.8 4-.6 1.8-3.6z"
      fill="white"
    />
    {ICON_CONTENT[variant] ?? ICON_CONTENT.program}
  </Icon>
);

const ICON_CONTENT: Partial<
  Record<MentorshipEmptyStateIconVariant, React.ReactNode>
> = {
  program: (
    <>
      <path
        d="M35 35h26a5 5 0 015 5v18H30V40a5 5 0 015-5z"
        stroke="#3B82F6"
        strokeWidth="4"
        strokeLinejoin="round"
      />
      <path
        d="M38 35v-8h20v8M41 27v-6h14v6"
        stroke="#3B82F6"
        strokeWidth="4"
        strokeLinejoin="round"
      />
    </>
  ),
  goal: (
    <>
      <circle cx="48" cy="46" r="19" stroke="#3B82F6" strokeWidth="4" />
      <circle cx="48" cy="46" r="10" stroke="#3B82F6" strokeWidth="4" />
      <circle cx="48" cy="46" r="3" fill="#3B82F6" />
    </>
  ),
  task: (
    <>
      <rect
        x="32"
        y="27"
        width="32"
        height="36"
        rx="5"
        stroke="#3B82F6"
        strokeWidth="4"
      />
      <path
        d="M40 40l5 5 11-12M40 55h16"
        stroke="#3B82F6"
        strokeWidth="4"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </>
  ),
  session: (
    <>
      <rect
        x="29"
        y="31"
        width="38"
        height="29"
        rx="5"
        stroke="#3B82F6"
        strokeWidth="4"
      />
      <path
        d="M38 25v10M58 25v10M29 42h38"
        stroke="#3B82F6"
        strokeWidth="4"
        strokeLinecap="round"
      />
    </>
  ),
  activity: (
    <>
      <path
        d="M33 54l9-13 9 9 10-17"
        stroke="#3B82F6"
        strokeWidth="4"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M31 63h35"
        stroke="#3B82F6"
        strokeWidth="4"
        strokeLinecap="round"
      />
    </>
  ),
  resource: (
    <>
      <path
        d="M33 29h24l7 8v27H33V29z"
        stroke="#3B82F6"
        strokeWidth="4"
        strokeLinejoin="round"
      />
      <path
        d="M56 29v10h8M41 48h14M41 57h10"
        stroke="#3B82F6"
        strokeWidth="4"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </>
  ),
  application: (
    <>
      <rect x="30" y="25" width="36" height="40" rx="5" stroke="#3B82F6" strokeWidth="4" />
      <path d="M39 36h18M39 46h18M39 56h10" stroke="#3B82F6" strokeWidth="4" strokeLinecap="round" />
      <path d="M55 55l4 4 8-10" stroke="#3B82F6" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round" />
    </>
  ),
  template: (
    <>
      <path d="M29 31h28l8 8v27H29V31z" stroke="#3B82F6" strokeWidth="4" strokeLinejoin="round" />
      <path d="M55 31v10h10M38 48h18M38 57h12" stroke="#3B82F6" strokeWidth="4" strokeLinecap="round" />
      <path d="M35 25h22" stroke="#3B82F6" strokeWidth="4" strokeLinecap="round" />
    </>
  ),
  matching: (
    <>
      <circle cx="37" cy="40" r="8" stroke="#3B82F6" strokeWidth="4" />
      <circle cx="59" cy="40" r="8" stroke="#3B82F6" strokeWidth="4" />
      <path d="M29 62c1-9 6-14 14-14M67 62c-1-9-6-14-14-14M43 40h10" stroke="#3B82F6" strokeWidth="4" strokeLinecap="round" />
    </>
  ),
  participants: (
    <>
      <circle cx="48" cy="36" r="9" stroke="#3B82F6" strokeWidth="4" />
      <circle cx="31" cy="42" r="6" stroke="#3B82F6" strokeWidth="4" />
      <circle cx="65" cy="42" r="6" stroke="#3B82F6" strokeWidth="4" />
      <path d="M32 64c1-10 6-16 16-16s15 6 16 16M22 62c1-7 4-11 10-12M74 62c-1-7-4-11-10-12" stroke="#3B82F6" strokeWidth="4" strokeLinecap="round" />
    </>
  ),
  cycle: (
    <>
      <path d="M63 36a18 18 0 00-29-4l-5 5M33 56a18 18 0 0029 4l5-5" stroke="#3B82F6" strokeWidth="4" strokeLinecap="round" />
      <path d="M29 28v9h9M67 64v-9h-9" stroke="#3B82F6" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round" />
    </>
  ),
  search: (
    <>
      <circle cx="44" cy="43" r="15" stroke="#3B82F6" strokeWidth="4" />
      <path d="M55 54l12 12M38 43h12" stroke="#3B82F6" strokeWidth="4" strokeLinecap="round" />
    </>
  ),
  notFound: (
    <>
      <path d="M33 29h24l7 8v27H33V29z" stroke="#3B82F6" strokeWidth="4" strokeLinejoin="round" />
      <path d="M56 29v10h8M41 49l14 14M55 49L41 63" stroke="#3B82F6" strokeWidth="4" strokeLinecap="round" />
    </>
  ),
};
